import * as React from 'react';
import { styled } from '@glitz/react';
import { ProductListWithAds } from 'webapp/product/ProductList';
import {
  desktopEdgeMarginDecorator,
  maxContentHugeWidthDecorator,
  maxContentNormalWidthDecorator,
  maxContentVeryWideWidthDecorator,
} from 'webapp/components/page-layout';
import { offsetTop, scrollPosition, useMatchSmallViewport } from '@avensia/excite';
import { useInfiniteScroll } from '@avensia/excite-ui';
import { InfiniteScrollWithPagination } from './InfiniteScrollWithPagination';
import { SeoSubpageProvider } from 'webapp/seo-subpages';
import { Filter } from 'webapp/pages/productListing/Filter';
import { isSvenssons } from 'webapp/theme';
import { useNinetailedFeatureTest } from 'webapp/ninetailed/NinetailedProvider';

type PropType = {
  id: string;
  name: string;
  products: NordicNest.Api.PartialProductApiModel[];
  facets: Excite.Esales.FacetApiModel[];
  highlightedFacets?: Excite.Esales.FacetApiModel[];
  selectedFacets: Excite.Esales.FacetApiModel[];
  pagination: Excite.ContentApi.PaginationApiModel;
  sortOptions: NordicNest.Api.SelectOptionApiModel[];
  ads?: NordicNest.Api.AdApiModel[];
  categoryNavigationHeading?: string;
  pageName?: string;
  subPageData?: NordicNest.Api.ProductListingPageApiModel['subPageData'];
  defaultPaginationSize?: number;
  retailMediaBannerAd?: NordicNest.Api.RetailMediaBanner;
  retailMediaSponsoredProducts?: NordicNest.Api.PartialProductApiModel[];
};

export default React.memo(function ProductListing(props: PropType) {
  const isMobile = useMatchSmallViewport();
  const { ref, bindInfiniteScroll } = useInfiniteScroll<HTMLDivElement>();
  const { variantKey, componentRef } = useNinetailedFeatureTest<HTMLDivElement>('wide_product_listing_page');

  const onChangePage = React.useCallback(() => {
    if (ref.current) {
      scrollPosition(offsetTop(ref.current) - (isSvenssons ? (isMobile ? 112 : 170) : 100));
    }
  }, [isMobile, ref]);

  return (
    <SeoSubpageProvider facets={props.facets} subPageData={props.subPageData}>
      {props.pagination && (
        <Filter
          containerRef={ref}
          totalProductCount={props.pagination.total}
          facets={props.facets}
          subPageData={props.subPageData}
          highlightedFacets={props.highlightedFacets}
          selectedFacets={props.selectedFacets}
          sortOptions={props.sortOptions}
          contentWidthDecorator={contentWidthDecorator(variantKey)}
        />
      )}
      <Base ref={componentRef} css={contentWidthDecorator(variantKey)}>
        <Listing ref={ref}>
          {props.products && (
            <ProductListWithAds
              id={props.id}
              name={props.name}
              products={props.products}
              ads={props.ads}
              gridVariant={variantKey}
              pagination={props.pagination}
              defaultPaginationSize={props.defaultPaginationSize}
              retailMediaBannerAd={props.retailMediaBannerAd}
              retailMediaSponsoredProducts={props.retailMediaSponsoredProducts}
            />
          )}
        </Listing>
        {props.pagination && (
          <InfiniteScrollWithPagination
            onChangePage={onChangePage}
            bindInfiniteScroll={bindInfiniteScroll}
            productCount={props.products.length}
            {...props.pagination}
          />
        )}
      </Base>
    </SeoSubpageProvider>
  );
});

const contentWidthDecorator = (variantKey?: string) => {
  switch (variantKey) {
    case 'max1920px':
      return maxContentVeryWideWidthDecorator;
    case 'max3440px':
      return maxContentHugeWidthDecorator;
    default:
      return maxContentNormalWidthDecorator;
  }
};

const Base = styled.div(desktopEdgeMarginDecorator, {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const Listing = styled.div({
  marginBottom: t => t.sharedSpacing.xxxxl,
});
